import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx'

type MappingValue = string | { template: string; transform: (value: any) => any }

interface Match {
    match: RegExpExecArray
    style: { [key: string]: any }
    startIndex: number
    endIndex: number
}

export function textToFile(content: string | object, filename: string) {
    const isJson = isJSON(content)
    const value = isJson ? JSON.stringify(content, null, 2) : String(content)
    const type = isJson ? "application/json" : "text/plain"
    const extension = isJson ? "json" : "txt"

    const blob = new Blob([value], { type: type })

    return new File([blob], `${filename}.${extension}`, { type: type })
}

export function isJSON(content: any): boolean {
    return (content && typeof content === "object")
}

export function generateMarkdownFromMapping(data: any, mapping: Record<string, MappingValue>) {
    let md = ""

    const groupedMap = groupMapping(mapping)

    if (groupedMap.topLevel) {
        for (const path of Object.keys(groupedMap.topLevel)) {
            const template = groupedMap.topLevel[path]
            const value = getValueFromPath(data, path)
            if (value != null) {
                md += applyTemplate(template, value) + "\n\n"
            }
        }
    }

    for (const arrayKey in groupedMap.arrays) {
        const arrayItems = getValueFromPath(data, arrayKey)
        if (Array.isArray(arrayItems)) {
            for (const item of arrayItems) {
                for (const [subPath, template] of Object.entries(groupedMap.arrays[arrayKey])) {
                    const value = getValueFromPath(item, subPath)
                    if (value != null) {
                        md += applyTemplate(template, value) + "\n\n"
                    }
                }
            }
        }
    }

    return md.trim()
}

interface GroupedMapping {
    topLevel: Record<string, MappingValue>
    arrays: Record<string, Record<string, MappingValue>>
}

function groupMapping(mapping: Record<string, MappingValue>): GroupedMapping {
    const grouped: GroupedMapping = { topLevel: {}, arrays: {} }

    for (const fullPath in mapping) {
        const template = mapping[fullPath]
        if (fullPath.includes("[]")) {
            const [arrKeyRaw, ...subPathParts] = fullPath.split("[]")
            const arrayKey = arrKeyRaw.replace(/\.$/, "")
            const subPath = subPathParts.join("").replace(/^\./, "")
            if (!grouped.arrays[arrayKey]) grouped.arrays[arrayKey] = {}
            grouped.arrays[arrayKey][subPath] = template
        } else {
            grouped.topLevel[fullPath] = template
        }
    }

    return grouped
}

function getValueFromPath(obj: any, path: string) {
    return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj)
}

function applyTemplate(template: MappingValue, value: any) {
    if (typeof template === "object" && template.template && template.transform) {
        const transformedValue = template.transform(value)
        if (template.template.includes("{list}") && Array.isArray(transformedValue)) {
            const list = transformedValue.map(item => `- ${item}`).join("\n")
            return template.template.replace("{list}", list)
        } else {
            return template.template.replace("{value}", transformedValue)
        }
    }

    if (typeof template === "string") {
        if (template.includes("{list}") && Array.isArray(value)) {
            const list = value.map(item => `- ${item}`).join("\n")
            return template.replace("{list}", list)
        } else {
            return template.replace("{value}", value)
        }
    }

    return String(value)
}

export const markdownToDocx = (text: string) => {
    const processedContent = convertLinks(processContentForMarkdown(text)) as string
    const paragraphs = parseMarkdownToDocx(processedContent)

    return new Document({
        sections: [{
            properties: {},
            children: paragraphs
        }],
        numbering: {
            config: [{
                reference: "default-numbering",
                levels: [
                    {
                        level: 0,
                        format: "bullet",
                        text: "•",
                        alignment: AlignmentType.LEFT,
                        style: {
                            paragraph: {
                                indent: { left: 432, hanging: 216 }
                            }
                        }
                    },
                    {
                        level: 1,
                        format: "bullet",
                        text: "◦",
                        alignment: AlignmentType.LEFT,
                        style: {
                            paragraph: {
                                indent: { left: 792, hanging: 216 }
                            }
                        }
                    },
                    {
                        level: 2,
                        format: "bullet",
                        text: "▪",
                        alignment: AlignmentType.LEFT,
                        style: {
                            paragraph: {
                                indent: { left: 1152, hanging: 216 }
                            }
                        }
                    }
                ]
            }]
        },
        styles: {
            default: {
                heading1: {
                    run: {
                        size: 32,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 240, after: 120 }
                    }
                },
                heading2: {
                    run: {
                        size: 28,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 200, after: 100 }
                    }
                },
                heading3: {
                    run: {
                        size: 24,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 160, after: 80 }
                    }
                },
                heading4: {
                    run: {
                        size: 20,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 120, after: 60 }
                    }
                },
                heading5: {
                    run: {
                        size: 16,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 80, after: 40 }
                    }
                },
                heading6: {
                    run: {
                        size: 13,
                        bold: true,
                        color: "111827"
                    },
                    paragraph: {
                        spacing: { before: 40, after: 20 }
                    }
                }
            }
        }
    })
}

const processContentForMarkdown = (content: string | object) => {
    if (typeof content !== "string") {
        return content as unknown as string
    }

    return content
}

const convertLinks = (content: string | object) => {
    if (typeof content !== "string") {
        return content as unknown as string
    }

    return content.replace(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi, (_, text, id) => {
        return `[${text}](/store/objects/${id})`
    })
}

const parseMarkdownToDocx = (markdown: string) => {
    const cleanedText = markdown.replace(/<!--.*?-->/gs, "")
    const lines = cleanedText.split('\n')
    const paragraphs: any[] = []
    let i = 0

    while (i < lines.length) {
        const line = lines[i].trim()

        // Skip empty lines
        if (!line) {
            i++
            continue
        }

        // Handle tables
        if (line.startsWith("|") && i + 1 < lines.length && lines[i + 1].trim().startsWith("|")) {
            const tableRows = []
            let rowIndex = i
            let isFirstRow = true
            let skipNextRow = false // For separator row

            while (rowIndex < lines.length && lines[rowIndex].trim().startsWith("|")) {
                const row = lines[rowIndex].trim()
                
                // Skip separator row (|---|---|)
                if (row.includes("---")) {
                    skipNextRow = true
                    rowIndex++
                    continue
                }

                const cells = row
                    .split("|")
                    .slice(1, -1)
                    .map(cell => cell.trim())

                const isHeaderRow = isFirstRow && !skipNextRow
                
                tableRows.push(
                    new TableRow({
                        children: cells.map(cellText =>
                            new TableCell({
                                children: [
                                    new Paragraph({
                                        children: parseInlineMarkdown(cellText, 22, isHeaderRow ? "374151" : "1F2937"),
                                        spacing: { before: 40, after: 40 } // Simulate padding
                                    })
                                ],
                                margins: {
                                    top: 80,    // py-2 ≈ 8px = 160 twips / 2
                                    bottom: 80,
                                    left: 160,  // px-4 ≈ 16px = 320 twips / 2
                                    right: 160
                                },
                                shading: isHeaderRow ? {
                                    type: "solid",
                                    color: "F3F4F6" // bg-gray-100
                                } : undefined,
                                borders: {
                                    top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                    bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                    left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                                    right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" }
                                }
                            })
                        )
                    })
                )

                if (isFirstRow) isFirstRow = false
                rowIndex++
            }

            if (tableRows.length > 0) {
                paragraphs.push(
                    new Table({
                        rows: tableRows,
                        width: {
                            size: 100,
                            type: WidthType.PERCENTAGE,
                        }
                    })
                )
            }

            i = rowIndex
            continue
        }

        // Headers
        if (line.startsWith('# ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(2), 32, "111827"),
                heading: HeadingLevel.HEADING_1,
                spacing: { before: 240, after: 120 }
            }))
        } else if (line.startsWith('## ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(3), 28, "111827"),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 100 }
            }))
        } else if (line.startsWith('### ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(4), 24, "111827"),
                heading: HeadingLevel.HEADING_3,
                spacing: { before: 160, after: 80 }
            }))
        } else if (line.startsWith('#### ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(5), 20, "111827"),
                heading: HeadingLevel.HEADING_4,
                spacing: { before: 120, after: 60 }
            }))
        } else if (line.startsWith('##### ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(6), 16, "111827"),
                heading: HeadingLevel.HEADING_5,
                spacing: { before: 80, after: 40 }
            }))
        } else if (line.startsWith('###### ')) {
            paragraphs.push(new Paragraph({
                children: parseInlineMarkdown(line.substring(7), 13, "111827"),
                spacing: { before: 40, after: 20 }
            }))
        } else if (line.startsWith('> ')) {
            // Blockquotes
            const blockquoteText = line.substring(2)
            paragraphs.push(new Paragraph({
                children: [
                    new TextRun({
                        text: blockquoteText,
                        italics: true,
                        color: "555555"
                    })
                ],
                indent: { left: 720 }, // Indent like border-left
                spacing: { before: 120, after: 120 }
            }))
        } else if (line.startsWith('```')) {
            // Code blocks
            const language = line.substring(3)
            i++
            let codeContent = ''

            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeContent += lines[i] + '\n'
                i++
            }

            if (language) {
                paragraphs.push(new Paragraph({
                    children: [
                        new TextRun({
                            text: language,
                            font: "Consolas",
                            size: 18,
                            color: "E5E7EB"
                        })
                    ],
                    shading: {
                        type: "solid",
                        color: "374151"
                    },
                    spacing: { before: 80 }
                }))
            }

            paragraphs.push(new Paragraph({
                children: [
                    new TextRun({
                        text: codeContent.trim(),
                        font: "Consolas",
                        size: 20,
                        color: "F3F4F6"
                    })
                ],
                shading: {
                    type: "solid",
                    color: "1F2937"
                },
                spacing: { after: 80 }
            }))
        } else if (line === '---' || line === '***') {
            // Horizontal rules
            paragraphs.push(new Paragraph({
                children: [new TextRun({ text: "" })],
                border: {
                    bottom: {
                        color: "D1D5DB",
                        space: 1,
                        style: BorderStyle.SINGLE,
                        size: 6
                    }
                },
                spacing: { before: 160, after: 160 }
            }))
        } else if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('  - ') || line.startsWith('    - ') || line.startsWith('• ') || line.startsWith('  • ')) {
            // Unordered lists (handle both - and * with proper indentation)
            const listItems = []
            while (i < lines.length) {
                const currentLine = lines[i]
                const trimmedLine = currentLine.trim()

                // Check if this line is a list item (handles various indentations and bullet types)
                if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ') || trimmedLine.startsWith('• ')) {
                    // Determine indentation level
                    const leadingSpaces = currentLine.length - currentLine.trimStart().length
                    const indentLevel = Math.floor(leadingSpaces / 2) // 2 spaces = 1 indent level

                    let itemText = ''
                    if (trimmedLine.startsWith('- ')) {
                        itemText = trimmedLine.substring(2)
                    } else if (trimmedLine.startsWith('* ')) {
                        itemText = trimmedLine.substring(2)
                    } else if (trimmedLine.startsWith('• ')) {
                        itemText = trimmedLine.substring(2)
                    }

                    listItems.push({ text: itemText, indent: indentLevel })
                    i++
                } else if (trimmedLine === '' && i + 1 < lines.length &&
                    (lines[i + 1].trim().startsWith('- ') || lines[i + 1].trim().startsWith('* ') || lines[i + 1].trim().startsWith('• '))) {
                    // Skip empty lines between list items
                    i++
                } else {
                    // Not a list item, break out of list parsing
                    break
                }
            }
            i-- // Adjust for the outer loop increment

            listItems.forEach(({ text, indent }) => {
                const leftIndent = 432 + (indent * 360) // Base indent + additional for each level

                paragraphs.push(new Paragraph({
                    children: parseInlineMarkdown(text, 22),
                    bullet: {
                        level: indent
                    },
                    indent: { left: leftIndent },
                    spacing: { before: 40, after: 40 }
                }))
            })
        } else if (/^\d+\.\s/.test(line) || /^\s+\d+\.\s/.test(line)) {
            // Ordered lists (handle nested numbering)
            const listItems = []
            let counter = 1
            while (i < lines.length) {
                const currentLine = lines[i]
                const trimmedLine = currentLine.trim()

                if (/^\d+\.\s/.test(trimmedLine)) {
                    // Determine indentation level
                    const leadingSpaces = currentLine.length - currentLine.trimStart().length
                    const indentLevel = Math.floor(leadingSpaces / 2)

                    const itemText = trimmedLine.replace(/^\d+\.\s/, '')
                    listItems.push({ text: itemText, indent: indentLevel })
                    i++
                } else if (trimmedLine === '' && i + 1 < lines.length &&
                    /^\s*\d+\.\s/.test(lines[i + 1])) {
                    // Skip empty lines between list items
                    i++
                } else {
                    break
                }
            }
            i-- // Adjust for the outer loop increment

            let counters = [0, 0, 0, 0] // Support up to 4 levels
            listItems.forEach(({ text, indent }) => {
                // Reset deeper level counters when going back to a shallower level
                for (let j = indent + 1; j < counters.length; j++) {
                    counters[j] = 0
                }
                counters[indent]++

                const leftIndent = 432 + (indent * 360)

                paragraphs.push(new Paragraph({
                    children: parseInlineMarkdown(text, 22),
                    numbering: {
                        reference: "default-numbering",
                        level: indent
                    },
                    indent: { left: leftIndent },
                    spacing: { before: 40, after: 40 }
                }))
            })
        } else {
            // Regular paragraphs
            const textRuns = parseInlineMarkdown(line)
            paragraphs.push(new Paragraph({
                children: textRuns,
                spacing: { before: 80, after: 80 }
            }))
        }

        i++
    }

    return paragraphs
}

const parseInlineMarkdown = (text: string, fontSize: number = 22, textColor: string = "1F2937"): TextRun[] => {
    const runs: TextRun[] = []
    let currentIndex = 0

    // Parse bold, italic, and inline code
    const patterns = [
        { regex: /\*\*(.*?)\*\*/g, style: { bold: true } },
        { regex: /\*(.*?)\*/g, style: { italics: true } },
        { regex: /`(.*?)`/g, style: { font: "Consolas", shading: { type: "solid", color: "F3F4F6" } as any } }
    ]

    let matches: Array<{ match: RegExpExecArray; style: any; startIndex: number; endIndex: number }> = []

    patterns.forEach(pattern => {
        let match
        const regex = new RegExp(pattern.regex.source, 'g')
        while ((match = regex.exec(text)) !== null) {
            matches.push({
                match,
                style: pattern.style,
                startIndex: match.index!,
                endIndex: match.index! + match[0].length
            })
        }
    })

    // Sort matches by position and remove overlapping matches
    matches.sort((a, b) => a.startIndex - b.startIndex)

    // Filter out overlapping matches (keep the first one)
    const filteredMatches: Match[] = [];
    for (let i = 0; i < matches.length; i++) {
        const current = matches[i]
        const isOverlapping = filteredMatches.some(prev =>
            current.startIndex < prev.endIndex && current.endIndex > prev.startIndex
        )
        if (!isOverlapping) {
            filteredMatches.push(current)
        }
    }

    filteredMatches.forEach(({ match, style, startIndex }) => {
        // Add text before match
        if (startIndex > currentIndex) {
            const plainText = text.substring(currentIndex, startIndex)
            if (plainText) {
                runs.push(new TextRun({
                    text: plainText,
                    color: "1F2937",
                    size: fontSize
                }))
            }
        }

        // Add styled text
        runs.push(new TextRun({
            text: match[1],
            color: style.font ? "374151" : "1F2937",
            size: fontSize,
            ...style
        }))

        currentIndex = startIndex + match[0].length
    })

    // Add remaining text
    if (currentIndex < text.length) {
        const remainingText = text.substring(currentIndex)
        if (remainingText) {
            runs.push(new TextRun({
                text: remainingText,
                color: "1F2937",
                size: fontSize
            }))
        }
    }

    // If no formatting found, return simple text
    if (runs.length === 0) {
        runs.push(new TextRun({
            text: text,
            color: textColor,
            size: fontSize
        }))
    }

    return runs
}